import React, { useState } from 'react';
import { Building, TrendingUp, TrendingDown, Clock, DollarSign, Calendar, BarChart3, AlertTriangle, CheckCircle, Wrench, Factory } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar, ComposedChart } from 'recharts';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const Immobilisations = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Données pour les graphiques de comparaison sur 5 ans
    const immobilisationsData = [
        { 
            annee: '2020', 
            valeurNetteComptable: 485000, 
            nouvellesAcquisitions: 65000, 
            dotationsAmortissements: 95000,
            tauxVetuste: 42,
            tauxRenouvellement: 8.5
        },
        { 
            annee: '2021', 
            valeurNetteComptable: 520000, 
            nouvellesAcquisitions: 85000, 
            dotationsAmortissements: 102000,
            tauxVetuste: 38,
            tauxRenouvellement: 12.2
        },
        { 
            annee: '2022', 
            valeurNetteComptable: 575000, 
            nouvellesAcquisitions: 120000, 
            dotationsAmortissements: 118000,
            tauxVetuste: 34,
            tauxRenouvellement: 15.8
        },
        { 
            annee: '2023', 
            valeurNetteComptable: 650000, 
            nouvellesAcquisitions: 145000, 
            dotationsAmortissements: 125000,
            tauxVetuste: 31,
            tauxRenouvellement: 18.5
        },
        { 
            annee: '2024', 
            valeurNetteComptable: 725000, 
            nouvellesAcquisitions: 165000, 
            dotationsAmortissements: 135000,
            tauxVetuste: 28,
            tauxRenouvellement: 22.1
        }
    ];

    // Répartition des immobilisations par catégorie
    const repartitionImmobilisations = [
        { name: 'Matériel industriel', value: 45, color: '#10b981', montant: 326250 },
        { name: 'Matériel de transport', value: 20, color: '#3b82f6', montant: 145000 },
        { name: 'Matériel informatique', value: 15, color: '#f59e0b', montant: 108750 },
        { name: 'Mobilier & agencements', value: 12, color: '#8b5cf6', montant: 87000 },
        { name: 'Autres', value: 8, color: '#ef4444', montant: 58000 }
    ];

    // Évolution mensuelle des acquisitions 2024
    const acquisitionsMensuelles = [
        { mois: 'Jan', acquisitions: 12000, amortissements: 11200 },
        { mois: 'Fév', acquisitions: 8500, amortissements: 11200 },
        { mois: 'Mar', acquisitions: 25000, amortissements: 11800 },
        { mois: 'Avr', acquisitions: 15000, amortissements: 11800 },
        { mois: 'Mai', acquisitions: 32000, amortissements: 12100 },
        { mois: 'Juin', acquisitions: 18000, amortissements: 12100 },
        { mois: 'Juil', acquisitions: 28000, amortissements: 12500 },
        { mois: 'Août', acquisitions: 26500, amortissements: 12500 }
    ];

    // État de vétusté par catégorie
    const vetusteParCategorie = [
        { categorie: 'Mat. industriel', pourcentage: 25, status: 'Bon' },
        { categorie: 'Transport', pourcentage: 35, status: 'Moyen' },
        { categorie: 'Informatique', pourcentage: 15, status: 'Excellent' },
        { categorie: 'Mobilier', pourcentage: 45, status: 'À surveiller' },
        { categorie: 'Autres', pourcentage: 30, status: 'Bon' }
    ];

    // Données actuelles 2024
    const currentYear = {
        valeurNetteComptable: 725000,
        nouvellesAcquisitions: 165000,
        dotationsAmortissements: 135000,
        tauxVetuste: 28,
        tauxRenouvellement: 22.1,
        valeurBrute: 985000,
        amortissementsCumules: 260000
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculatePercentage = (current, previous) => {
        if (!previous) return 0;
        return ((current - previous) / previous * 100).toFixed(1);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${formatCurrency(entry.value)}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomPercentTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`Année ${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${entry.value}%`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Excellent': return '#10b981';
            case 'Bon': return '#3b82f6';
            case 'Moyen': return '#f59e0b';
            case 'À surveiller': return '#ef4444';
            default: return '#6b7280';
        }
    };

    return (
        <>
            <div className="app-container">
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

                <div className="main-wrapper">
                    <Navbar onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

                    <main className="main-content">
                        {/* Header Section */}
                        <div className="page-header">
                            <div className="header-content">
                                <div className="header-text">
                                    <h1 className="page-title">État des Immobilisations</h1>
                                    <p className="page-subtitle">Suivi et analyse du patrimoine immobilier de votre entreprise</p>
                                </div>
                            </div>
                        </div>

                        {/* KPIs Cards */}
                        <div className="kpis-grid">
                            <div className="kpi-card primary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Valeur Nette Comptable</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.valeurNetteComptable)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.valeurNetteComptable, immobilisationsData[3].valeurNetteComptable)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Building size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card success">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Nouvelles Acquisitions</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.nouvellesAcquisitions)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.nouvellesAcquisitions, immobilisationsData[3].nouvellesAcquisitions)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingUp size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card info">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Dotations aux Amortissements</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.dotationsAmortissements)}</p>
                                        <span className="kpi-trend neutral">
                                            +{calculatePercentage(currentYear.dotationsAmortissements, immobilisationsData[3].dotationsAmortissements)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingDown size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card warning">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Taux de Vétusté</h3>
                                        <p className="kpi-value">{currentYear.tauxVetuste}%</p>
                                        <span className="kpi-trend positive">
                                            -{Math.abs(calculatePercentage(currentYear.tauxVetuste, immobilisationsData[3].tauxVetuste))}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <AlertTriangle size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card secondary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Taux de Renouvellement</h3>
                                        <p className="kpi-value">{currentYear.tauxRenouvellement}%</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.tauxRenouvellement, immobilisationsData[3].tauxRenouvellement)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Wrench size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card danger">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Amortissements Cumulés</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.amortissementsCumules)}</p>
                                        <span className="kpi-trend neutral">
                                            {((currentYear.amortissementsCumules / currentYear.valeurBrute) * 100).toFixed(1)}% du brut
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Factory size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="charts-section">
                            {/* Evolution Valeur Nette Comptable sur 5 ans */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution de la Valeur Nette Comptable (5 dernières années)</h3>
                                    <div className="chart-badge">Croissance soutenue</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <AreaChart data={immobilisationsData}>
                                            <defs>
                                                <linearGradient id="colorVNC" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area
                                                type="monotone"
                                                dataKey="valeurNetteComptable"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorVNC)"
                                                dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8, stroke: '#10b981', strokeWidth: 2 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Acquisitions vs Amortissements */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Acquisitions vs Amortissements (5 ans)</h3>
                                    <div className="chart-badge secondary">Comparaison annuelle</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <ComposedChart data={immobilisationsData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="nouvellesAcquisitions"
                                                name="Nouvelles Acquisitions"
                                                fill="#10b981"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="dotationsAmortissements"
                                                name="Dotations Amortissements"
                                                stroke="#ef4444"
                                                strokeWidth={3}
                                                dot={{ fill: '#ef4444', strokeWidth: 2, r: 5 }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Évolution mensuelle 2024 */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution Mensuelle 2024</h3>
                                    <div className="chart-badge warning">Année en cours</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={acquisitionsMensuelles}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="mois"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="acquisitions"
                                                name="Acquisitions"
                                                fill="#3b82f6"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Bar
                                                dataKey="amortissements"
                                                name="Amortissements"
                                                fill="#f59e0b"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Taux de Vétusté et Renouvellement */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Taux de Vétusté vs Renouvellement</h3>
                                    <div className="chart-badge success">Tendance favorable</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={immobilisationsData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                label={{ value: '%', angle: -90, position: 'insideLeft' }}
                                            />
                                            <Tooltip content={<CustomPercentTooltip />} />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="tauxVetuste"
                                                name="Taux de Vétusté"
                                                stroke="#ef4444"
                                                strokeWidth={3}
                                                dot={{ fill: '#ef4444', strokeWidth: 2, r: 5 }}
                                                activeDot={{ r: 7 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="tauxRenouvellement"
                                                name="Taux de Renouvellement"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }}
                                                activeDot={{ r: 7 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Répartition par catégorie */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition par Catégorie</h3>
                                    <div className="chart-badge info">Structure équilibrée</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={repartitionImmobilisations}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}%`}
                                                labelLine={false}
                                            >
                                                {repartitionImmobilisations.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value, name, props) => [
                                                `${value}% (${formatCurrency(props.payload.montant)})`,
                                                name
                                            ]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* État de vétusté par catégorie */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">État de Vétusté par Catégorie</h3>
                                    <div className="chart-badge warning">Analyse détaillée</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={vetusteParCategorie} layout="horizontal">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                type="number"
                                                domain={[0, 50]}
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${value}%`}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="categorie"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <Tooltip 
                                                formatter={(value, name, props) => [
                                                    `${value}% - ${props.payload.status}`,
                                                    'Taux de vétusté'
                                                ]} 
                                            />
                                            <Bar
                                                dataKey="pourcentage"
                                                radius={[0, 8, 8, 0]}
                                                fill={(entry) => getStatusColor(entry.status)}
                                            >
                                                {vetusteParCategorie.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Commentaires du Cabinet */}
                        <div className="commentaires-section">
                            <div className="commentaires-card">
                                <div className="commentaires-header">
                                    <h3 className="commentaires-title">Commentaires du Cabinet CINEF-EC</h3>
                                    <div className="commentaires-date">
                                        Dernière mise à jour: {formatDate('2024-08-20')}
                                    </div>
                                </div>
                                <div className="commentaires-content">
                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Analyse du Patrimoine Immobilier</h4>
                                        <p className="commentaire-text">
                                            La valeur nette comptable des immobilisations atteint {formatCurrency(currentYear.valeurNetteComptable)},
                                            en progression de {calculatePercentage(currentYear.valeurNetteComptable, immobilisationsData[3].valeurNetteComptable)}% 
                                            par rapport à 2023. Cette croissance témoigne d'une politique d'investissement dynamique et d'un 
                                            renouvellement approprié de l'outil de production.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Gestion des Amortissements</h4>
                                        <p className="commentaire-text">
                                            Les nouvelles acquisitions de {formatCurrency(currentYear.nouvellesAcquisitions)} dépassent 
                                            les dotations aux amortissements de {formatCurrency(currentYear.dotationsAmortissements)}, 
                                            indiquant un effort d'investissement supérieur à la dépréciation. Le taux de vétusté de {currentYear.tauxVetuste}% 
                                            reste dans des limites acceptables et continue de diminuer.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Recommandations Stratégiques</h4>
                                        <p className="commentaire-text">
                                            Le taux de renouvellement de {currentYear.tauxRenouvellement}% est excellent et témoigne d'une 
                                            modernisation continue de l'outil de production. Nous recommandons de maintenir cette politique 
                                            d'investissement tout en surveillant particulièrement le matériel de transport et le mobilier 
                                            qui présentent des taux de vétusté plus élevés.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .app-container {
                    display: flex;
                    height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    position: relative;
                    overflow: hidden;
                }

                .app-container::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    pointer-events: none;
                }

                .main-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    margin-left: 320px;
                    z-index: 1;
                }

                .main-content {
                    flex: 1;
                    padding: 32px;
                    overflow-y: auto;
                    scrollbar-width: thin;
                    scrollbar-color: rgba(16, 185, 129, 0.3) transparent;
                }

                .main-content::-webkit-scrollbar {
                    width: 6px;
                }

                .main-content::-webkit-scrollbar-track {
                    background: transparent;
                }

                .main-content::-webkit-scrollbar-thumb {
                    background: linear-gradient(45deg, rgba(16, 185, 129, 0.3), rgba(59, 130, 246, 0.3));
                    border-radius: 10px;
                }

                /* Header Section */
                .page-header {
                    margin-bottom: 32px;
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .header-text {
                    flex: 1;
                }

                .page-title {
                    font-size: 36px;
                    font-weight: 800;
                    margin: 0 0 8px 0;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
                }

                .page-subtitle {
                    font-size: 16px;
                    color: #64748b;
                    margin: 0;
                    font-weight: 500;
                }

                /* KPIs Grid */
                .kpis-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                    gap: 24px;
                    margin-bottom: 40px;
                }

                .kpi-card {
                    background: rgba(255, 255, 255, 0.95);
                    border-radius: 20px;
                    padding: 24px;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
                }

                .kpi-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    position: relative;
                    z-index: 2;
                }

                .kpi-info {
                    flex: 1;
                }

                .kpi-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    margin: 0 0 8px 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .kpi-value {
                    font-size: 28px;
                    font-weight: 800;
                    color: #1e293b;
                    margin: 0 0 4px 0;
                    line-height: 1.2;
                }

                .kpi-trend {
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 8px;
                    border-radius: 12px;
                    display: inline-block;
                }

                .kpi-trend.positive {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                }

                .kpi-trend.negative {
                    background: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                }

                .kpi-trend.neutral {
                    background: rgba(107, 114, 128, 0.1);
                    color: #6b7280;
                }

                .kpi-icon {
                    padding: 12px;
                    border-radius: 16px;
                    background: rgba(255, 255, 255, 0.8);
                    backdrop-filter: blur(10px);
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                }

                .kpi-card.primary .kpi-icon {
                    color: #10b981;
                    background: rgba(16, 185, 129, 0.1);
                }

                .kpi-card.success .kpi-icon {
                    color: #10b981;
                    background: rgba(16, 185, 129, 0.1);
                }

                .kpi-card.info .kpi-icon {
                    color: #3b82f6;
                    background: rgba(59, 130, 246, 0.1);
                }

                .kpi-card.warning .kpi-icon {
                    color: #f59e0b;
                    background: rgba(245, 158, 11, 0.1);
                }

                .kpi-card.secondary .kpi-icon {
                    color: #8b5cf6;
                    background: rgba(139, 92, 246, 0.1);
                }

                .kpi-card.danger .kpi-icon {
                    color: #ef4444;
                    background: rgba(239, 68, 68, 0.1);
                }

                .kpi-gradient {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    opacity: 0.05;
                    pointer-events: none;
                }

                .kpi-card.primary .kpi-gradient {
                    background: linear-gradient(135deg, #10b981, #059669);
                }

                .kpi-card.success .kpi-gradient {
                    background: linear-gradient(135deg, #10b981, #059669);
                }

                .kpi-card.info .kpi-gradient {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                }

                .kpi-card.warning .kpi-gradient {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                }

                .kpi-card.secondary .kpi-gradient {
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                }

                .kpi-card.danger .kpi-gradient {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                }

                /* Charts Section */
                .charts-section {
                    display: grid;
                    grid-template-columns: repeat(12, 1fr);
                    gap: 24px;
                    margin-bottom: 40px;
                }

                .chart-card {
                    grid-column: span 6;
                    background: rgba(255, 255, 255, 0.95);
                    border-radius: 20px;
                    padding: 24px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .chart-card.full-width {
                    grid-column: span 12;
                }

                .chart-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
                }

                .chart-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid rgba(226, 232, 240, 0.8);
                }

                .chart-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .chart-badge {
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                    border: 1px solid rgba(16, 185, 129, 0.2);
                }

                .chart-badge.secondary {
                    background: rgba(59, 130, 246, 0.1);
                    color: #2563eb;
                    border-color: rgba(59, 130, 246, 0.2);
                }

                .chart-badge.warning {
                    background: rgba(245, 158, 11, 0.1);
                    color: #d97706;
                    border-color: rgba(245, 158, 11, 0.2);
                }

                .chart-badge.success {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                    border-color: rgba(16, 185, 129, 0.2);
                }

                .chart-badge.info {
                    background: rgba(59, 130, 246, 0.1);
                    color: #2563eb;
                    border-color: rgba(59, 130, 246, 0.2);
                }

                .chart-content {
                    position: relative;
                }

                /* Custom Tooltip Styles */
                .custom-tooltip {
                    background: rgba(255, 255, 255, 0.95);
                    padding: 12px 16px;
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .tooltip-label {
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 4px;
                    font-size: 14px;
                }

                .tooltip-value {
                    font-weight: 500;
                    margin: 2px 0;
                    font-size: 13px;
                }

                /* Commentaires Section */
                .commentaires-section {
                    margin-top: 40px;
                }

                .commentaires-card {
                    background: rgba(255, 255, 255, 0.95);
                    border-radius: 20px;
                    padding: 32px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .commentaires-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
                }

                .commentaires-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid rgba(16, 185, 129, 0.1);
                }

                .commentaires-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .commentaires-date {
                    font-size: 14px;
                    color: #64748b;
                    font-weight: 500;
                    padding: 8px 16px;
                    background: rgba(100, 116, 139, 0.1);
                    border-radius: 20px;
                }

                .commentaires-content {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                .commentaire-item {
                    padding: 20px;
                    background: rgba(248, 250, 252, 0.8);
                    border-radius: 16px;
                    border-left: 4px solid #10b981;
                    transition: all 0.3s ease;
                }

                .commentaire-item:hover {
                    background: rgba(248, 250, 252, 1);
                    transform: translateX(4px);
                }

                .commentaire-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 12px 0;
                }

                .commentaire-text {
                    font-size: 14px;
                    line-height: 1.6;
                    color: #475569;
                    margin: 0;
                }

                /* Responsive Design */
                @media (max-width: 1200px) {
                    .main-wrapper {
                        margin-left: 0;
                    }

                    .charts-section {
                        grid-template-columns: 1fr;
                    }

                    .chart-card {
                        grid-column: span 1;
                    }

                    .chart-card.full-width {
                        grid-column: span 1;
                    }
                }

                @media (max-width: 768px) {
                    .main-content {
                        padding: 20px;
                    }

                    .kpis-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .page-title {
                        font-size: 28px;
                    }

                    .kpi-value {
                        font-size: 24px;
                    }

                    .chart-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .commentaires-card {
                        padding: 24px;
                    }

                    .commentaires-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .commentaires-title {
                        font-size: 20px;
                    }
                }

                @media (max-width: 480px) {
                    .main-content {
                        padding: 16px;
                    }

                    .kpi-card {
                        padding: 20px;
                    }

                    .chart-card {
                        padding: 20px;
                    }

                    .commentaires-card {
                        padding: 20px;
                    }

                    .kpi-content {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 16px;
                    }

                    .kpi-icon {
                        align-self: flex-end;
                    }
                }

                /* Animation keyframes */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                /* Apply animations */
                .kpi-card {
                    animation: fadeInUp 0.6s ease-out;
                }

                .chart-card {
                    animation: fadeInUp 0.8s ease-out;
                }

                .commentaires-card {
                    animation: slideInLeft 1s ease-out;
                }

                /* Stagger animation delays */
                .kpi-card:nth-child(1) { animation-delay: 0.1s; }
                .kpi-card:nth-child(2) { animation-delay: 0.2s; }
                .kpi-card:nth-child(3) { animation-delay: 0.3s; }
                .kpi-card:nth-child(4) { animation-delay: 0.4s; }
                .kpi-card:nth-child(5) { animation-delay: 0.5s; }
                .kpi-card:nth-child(6) { animation-delay: 0.6s; }

                .chart-card:nth-child(1) { animation-delay: 0.2s; }
                .chart-card:nth-child(2) { animation-delay: 0.4s; }
                .chart-card:nth-child(3) { animation-delay: 0.6s; }
                .chart-card:nth-child(4) { animation-delay: 0.8s; }
            `}</style>
        </>
    );
};

export default Immobilisations;